// Badges (earned by spotting cars or garage size)
export interface Badge {
  id: string;
  name: string;
  requirement: number;
  tier: 'green' | 'blue' | 'purple';
  rank: number;
  category: 'spotter' | 'garage';
}

// User & auth
export interface User {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  location?: string;
  topBadges?: Badge[];
}

// Car identification (from spot or garage)
export interface CarIdentification {
  makeId?: string;
  make: string;
  model: string;
  yearRange?: string; // e.g. "2018-2022"
  year?: number;
  bodyStyle?: string; // Car type: Sedan, Coupe, Convertible, etc.
  powertrainType?: PowertrainType;
  performanceType?: string; // Sport, Luxury, Grand Tourer, etc.
  trim?: string;
  generation?: string;
}

// Spotted car post (feed item)
export interface Spot {
  id: string;
  authorId: string;
  author?: User;
  imageUrl: string;
  images: string[];
  caption?: string;
  car: CarIdentification & {
    mods?: string;
    wheels?: string;
    badges?: string;
    userNotes?: string;
  };
  location?: string;
  city?: string;
  countryCode?: string;
  event?: string;
  taggedOwnerId?: string;
  taggedOwner?: User;
  likes: string[];
  beautiful: string[];
  cool: string[];
  sweetPhoto: string[];
  comments: Comment[];
  likeCount?: number;
  beautifulCount?: number;
  coolCount?: number;
  sweetPhotoCount?: number;
  commentCount?: number;
  likedByCurrentUser?: boolean;
  beautifulByCurrentUser?: boolean;
  coolByCurrentUser?: boolean;
  sweetPhotoByCurrentUser?: boolean;
  isSummary?: boolean;
  createdAt: string; // ISO
  updatedAt?: string;
  viewCount: number;
}

export interface Comment {
  id: string;
  authorId: string;
  author?: User;
  parentCommentId?: string;
  replyToCommentId?: string;
  body: string;
  createdAt: string;
}

// User's own garage car (detailed profile)
export interface GarageCarImage {
  url: string;
  caption?: string;
}

export const GARAGE_MOD_CATEGORIES = [
  'Body',
  'Exhaust',
  'Engine',
  'Suspension',
  'Wheels',
  'Interior',
  'Lighting',
] as const;
export type GarageModCategory = (typeof GARAGE_MOD_CATEGORIES)[number];

export interface GarageModImage {
  url: string;
  caption?: string;
}

export interface GarageMod {
  id: string;
  garageCarId: string;
  category: GarageModCategory;
  title: string;
  details?: string;
  dateCompleted?: string; // YYYY-MM-DD
  images: GarageModImage[];
  createdAt: string;
  updatedAt?: string;
}

/** Track session event types for garage car track sessions */
export const TRACK_SESSION_EVENT_TYPES = [
  'Track day',
  'Time trial',
  'HPDE',
  'Racing',
  'Autocross',
  'Training',
] as const;
export type TrackSessionEventType = (typeof TRACK_SESSION_EVENT_TYPES)[number];

export interface TrackSession {
  id: string;
  garageCarId: string;
  venueName: string;
  sessionDate: string; // YYYY-MM-DD
  eventName?: string;
  eventType: TrackSessionEventType | string;
  bestLapTimeSeconds: number | null;
  bestLapTimeDisplay: string;
  slowestLapTimeSeconds: number | null;
  slowestLapTimeDisplay: string;
  lapCount: number | null;
  tiresUsed?: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export const POWERTRAIN_TYPES = ['gas', 'hybrid', 'electric'] as const;
export type PowertrainType = (typeof POWERTRAIN_TYPES)[number];

export interface MaintenanceRecord {
  id: string;
  garageCarId: string;
  serviceType: string;
  title: string;
  performedOn: string;
  performedOdometer?: number | null;
  notes?: string;
  cost?: number | null;
  shopName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface MaintenanceScheduleItem {
  serviceType: string;
  title: string;
  description: string;
  intervalMiles?: number;
  intervalMonths?: number;
  dueDate?: string;
  predictedDueDate?: string;
  dueOdometer?: number;
  remainingMiles?: number;
  status: 'upcoming' | 'due_soon' | 'overdue';
  dueReason: 'time' | 'mileage' | 'time_and_mileage' | 'inspection';
  lastCompletedOn?: string;
  lastCompletedOdometer?: number | null;
}

export interface GarageCar {
  id: string;
  ownerId: string;
  name?: string; // e.g. "Daily", "Weekend toy"
  car: CarIdentification;
  year?: number;
  trim?: string;
  engine?: string;
  /** Legacy flat list; prefer mods (structured) when available. */
  mods?: string[];
  /** Structured mod entries with category, title, details, date, images. */
  garageMods?: GarageMod[];
  /** Track sessions (lap times, venue, event type, etc.). */
  trackSessions?: TrackSession[];
  performanceSpecs?: Record<string, string>; // e.g. { "0-60": "4.2s", "HP": "450" }
  colorCode?: string;
  colorName?: string;
  vin?: string;
  currentOdometer?: number | null;
  averageMilesPerMonth?: number | null;
  maintenanceRemindersEnabled?: boolean;
  lastOdometerUpdatedAt?: string;
  /** Ordered list; first is cover. Use images when captions/order matter. */
  images: GarageCarImage[];
  /** Ordered URLs (same order as images). Kept for backward compat; prefer images. */
  photos: string[];
  buildProgressPhotos?: { url: string; caption?: string; date?: string }[];
  isPastCar?: boolean;
  acquiredDate?: string;
  soldDate?: string | null;
  /** When true, sale details are shown and sale fields may be set. */
  forSale?: boolean;
  saleSellingDetails?: string;
  saleCarCondition?: string;
  saleCarfaxLink?: string;
  saleCarFeatures?: string;
  salePrice?: string;
  saleMilesDriven?: number | null;
  isPrimary?: boolean;
  likes: string[];
  comments: Comment[];
  maintenanceRecords?: MaintenanceRecord[];
  maintenanceSchedule?: MaintenanceScheduleItem[];
  createdAt: string;
  updatedAt?: string;
}

// Favorites list
export interface FavoriteList {
  id: string;
  userId: string;
  name: string;
  spotIds: string[];
  createdAt: string;
}
